import type { Action } from '@reduxjs/toolkit'
import { ignoreElements, tap, withLatestFrom } from 'rxjs'
import type { Epic } from '../../index.ts'
import { orderWizardActions as wizard } from '../../Slice/OrderWizard/OrderWizardSlice.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { reportAnalysis, warnOnce } from '../../../Services/OrderWizard/analysisTelemetry.ts'
import {
  detectionSummary,
  draftDetectionSummary,
  estimateSummary,
  estimatesFoundNothing,
  failedDetectionSummary,
  primersMissingOnUntrimmed,
  sheetSummary,
  uploadSummary
} from '../../../Services/OrderWizard/analysisSummaries.ts'
import type { UploadEvent } from '../../../Services/OrderWizard/uploadManager.ts'
import type { RootState } from './draftState.ts'
import { draftOf } from './draftState.ts'

const report = (action: Action, draft: DraftState | undefined) => {
  if (wizard.detectionCompleted.match(action)) {
    const summary = detectionSummary(action.payload, draft)
    reportAnalysis(summary)
    if (draft && primersMissingOnUntrimmed(draft)) {
      warnOnce(draft.id, 'primers_not_found', summary)
    }
  } else if (wizard.detectionFailed.match(action)) {
    reportAnalysis(failedDetectionSummary(action.payload.keys?.length))
  } else if (wizard.questionAnswered.match(action)) {
    if (action.payload.answer.kind === 'sheet-key-column' && draft) {
      reportAnalysis(sheetSummary(draft, 'remapped'))
    }
    if (draft && primersMissingOnUntrimmed(draft)) {
      warnOnce(draft.id, 'primers_not_found', draftDetectionSummary(draft))
    }
  } else if (wizard.estimatesAdded.match(action) && draft) {
    const summary = estimateSummary(action.payload.estimates, draft)
    reportAnalysis(summary)
    if (summary.expects_primers && estimatesFoundNothing(summary)) {
      warnOnce(draft.id, 'primers_not_found', summary)
    }
  } else if (wizard.sheetLoaded.match(action) && draft) {
    reportAnalysis(sheetSummary(draft, 'loaded'))
  } else if (wizard.sheetMappingChanged.match(action) && draft) {
    reportAnalysis(sheetSummary(draft, 'remapped'))
  } else if (wizard.sheetFailed.match(action) && draft) {
    reportAnalysis(sheetSummary(draft, 'unreadable'))
  }
}

/**
 * One debugging summary per analysis step, read from the draft as the step's result left it. Telemetry
 * only: it emits no actions, and a failure in it is swallowed.
 */
export const analysisTelemetryEpic: Epic = (action$, state$) =>
  action$.pipe(
    withLatestFrom(state$),
    tap(([action, state]: [Action, RootState]) => {
      try {
        const draftId = (action as { payload?: { draftId?: unknown } }).payload?.draftId
        report(action, typeof draftId === 'string' ? draftOf(state, draftId) : undefined)
      } catch {
        /* telemetry must never break the wizard */
      }
    }),
    ignoreElements()
  )

/* a clean success is only counted: a breadcrumb per file would push the analysis steps out of the trail */
export const reportUploadEvent = (event: UploadEvent): void => {
  try {
    const summary = uploadSummary(event)
    if (!summary) {
      return
    }
    reportAnalysis(summary, { breadcrumb: summary.outcome !== 'succeeded' || !!summary.retries })
    if (summary.outcome === 'failed') {
      warnOnce(event.draftId, 'upload_failed', summary)
    }
  } catch {
    /* telemetry must never break the uploads */
  }
}
